import type { BudgetNoteRowDraft, BudgetNoteUsageEntryDraft } from '../types/budgetNote';
import {
  DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS,
  normalizeBudgetNoteCategoryHeaderLabels,
  type BudgetNoteCategoryHeaderLabels,
} from './budgetNoteCategoryHeaderLabels';
import { normalizeBudgetSuppApplyHistory } from './budgetSuppApplyHistory';

export type { BudgetNoteCategoryHeaderLabels };

export interface BudgetNoteWorkspaceSnapshot {
  rows: BudgetNoteRowDraft[];
  entries: BudgetNoteUsageEntryDraft[];
  categoryHeaderLabels: BudgetNoteCategoryHeaderLabels;
}

/** v3: contentVersion(head) 비교용 메타 저장 */
const BUDGET_NOTE_CACHE_VERSION = 'v3';

type BudgetNoteWorkspaceCachePayload = BudgetNoteWorkspaceSnapshot & {
  savedAt: number;
  /** rows|entries|settings 각 `count:max(updated_at)` 결합 */
  contentVersion?: string;
};

function cacheScopeKey(noteWorkspaceId: string | null): string {
  return noteWorkspaceId ?? 'account';
}

function storageKey(userId: string, noteWorkspaceId: string | null): string {
  return `cp.budgetNoteWorkspace.${BUDGET_NOTE_CACHE_VERSION}.${userId}.${cacheScopeKey(noteWorkspaceId)}`;
}

function mapRow(r: BudgetNoteRowDraft, noteWorkspaceId: string | null): BudgetNoteRowDraft {
  return {
    ...r,
    workspace_id: r.workspace_id ?? noteWorkspaceId,
    current_budget: Number(r.current_budget) || 0,
    supp_budget: r.supp_budget === null || r.supp_budget === undefined ? null : Number(r.supp_budget),
    project_budget: Number(r.project_budget) || 0,
    cat1_color: (r.cat1_color ?? '').trim(),
    cat2_color: (r.cat2_color ?? '').trim(),
    cat3_color: (r.cat3_color ?? '').trim(),
    cat4_color: (r.cat4_color ?? '').trim(),
    memo_color: (r.memo_color ?? '').trim(),
    is_hidden: Boolean(r.is_hidden),
    supp_apply_history: normalizeBudgetSuppApplyHistory(r.supp_apply_history),
  };
}

function mapEntry(x: BudgetNoteUsageEntryDraft, noteWorkspaceId: string | null): BudgetNoteUsageEntryDraft {
  return {
    ...x,
    workspace_id: x.workspace_id ?? noteWorkspaceId,
    amount: Number(x.amount) || 0,
  };
}

function normalizeSnapshot(
  snapshot: Partial<BudgetNoteWorkspaceSnapshot>,
  noteWorkspaceId: string | null
): BudgetNoteWorkspaceSnapshot {
  return {
    rows: Array.isArray(snapshot.rows) ? snapshot.rows.map((r) => mapRow(r, noteWorkspaceId)) : [],
    entries: Array.isArray(snapshot.entries) ? snapshot.entries.map((x) => mapEntry(x, noteWorkspaceId)) : [],
    categoryHeaderLabels: normalizeBudgetNoteCategoryHeaderLabels(snapshot.categoryHeaderLabels),
  };
}

export function loadBudgetNoteWorkspaceCache(
  userId: string,
  noteWorkspaceId: string | null
): {
  found: boolean;
  data: BudgetNoteWorkspaceSnapshot;
  savedAt?: number;
  contentVersion: string | null;
} {
  const empty: BudgetNoteWorkspaceSnapshot = {
    rows: [],
    entries: [],
    categoryHeaderLabels: { ...DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS },
  };
  if (!userId || typeof window === 'undefined') {
    return { found: false, data: empty, contentVersion: null };
  }
  try {
    const raw = window.localStorage.getItem(storageKey(userId, noteWorkspaceId));
    if (!raw) return { found: false, data: empty, contentVersion: null };
    const parsed = JSON.parse(raw) as Partial<BudgetNoteWorkspaceCachePayload> | null;
    if (!parsed || typeof parsed !== 'object') return { found: false, data: empty, contentVersion: null };
    const data = normalizeSnapshot(parsed, noteWorkspaceId);
    const savedAt = typeof parsed.savedAt === 'number' && parsed.savedAt > 0 ? parsed.savedAt : undefined;
    const contentVersion =
      typeof parsed.contentVersion === 'string' && parsed.contentVersion.trim()
        ? parsed.contentVersion.trim()
        : null;
    return { found: true, data, savedAt, contentVersion };
  } catch {
    return { found: false, data: empty, contentVersion: null };
  }
}

export function saveBudgetNoteWorkspaceCache(
  userId: string,
  noteWorkspaceId: string | null,
  snapshot: BudgetNoteWorkspaceSnapshot,
  options?: { contentVersion?: string }
): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    const contentVersion = (options?.contentVersion ?? '').trim();
    const payload: BudgetNoteWorkspaceCachePayload = {
      ...normalizeSnapshot(snapshot, noteWorkspaceId),
      savedAt: Date.now(),
      ...(contentVersion ? { contentVersion } : {}),
    };
    window.localStorage.setItem(storageKey(userId, noteWorkspaceId), JSON.stringify(payload));
  } catch {
    // ignore quota/private mode errors
  }
}

export function invalidateBudgetNoteWorkspaceCache(userId: string, noteWorkspaceId: string | null): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(storageKey(userId, noteWorkspaceId));
  } catch {
    // ignore
  }
}
